/**
 * Утилиты для управления качеством видео
 * Используем только setPublishingQuality/setVideoQuality для выбора слоя simulcast.
 * Все ограничения заданы в publishDefaults.videoSimulcastLayers.
 */

import {
    LocalParticipant,
    LocalTrackPublication,
    LocalVideoTrack,
    Participant,
    RemoteTrackPublication,
    Track,
    VideoQuality,
} from 'livekit-client';
import {
    LARGE_ROOM_PARTICIPANTS_THRESHOLD,
    ROOM_MODE,
    RoomMode,
    SCREEN_SHARE_BITRATE_LIMITS,
    VIDEO_QUALITY_THRESHOLDS,
} from '../consts';

type ExtendedRemoteTrackPublication = RemoteTrackPublication & {
    videoQuality?: VideoQuality;
    setVideoQuality?: (quality: VideoQuality) => void;
};

type RTCRtpSendParametersWithEncodings = RTCRtpSendParameters & {
    encodings?: Array<{
        maxBitrate?: number;
        maxFramerate?: number;
        [key: string]: unknown;
    }>;
};

const SCREEN_SHARE_QUALITY_TO_BITRATE = {
    [VideoQuality.HIGH]: SCREEN_SHARE_BITRATE_LIMITS.HIGH,
    [VideoQuality.MEDIUM]: SCREEN_SHARE_BITRATE_LIMITS.MEDIUM,
    [VideoQuality.LOW]: SCREEN_SHARE_BITRATE_LIMITS.LOW,
} as const;

const getScreenShareBitrate = (quality: VideoQuality) =>
    SCREEN_SHARE_QUALITY_TO_BITRATE[quality] || SCREEN_SHARE_BITRATE_LIMITS.MEDIUM;

const applyRtpSenderParameters = (
    sender: RTCRtpSender,
    options: { maxBitrate: number; maxFramerate?: number },
): void => {
    if (!sender?.getParameters) return;

    try {
        const params = sender.getParameters() as RTCRtpSendParametersWithEncodings;
        if (!params.encodings?.length) return;

        // Проверяем, нужно ли обновлять параметры (избегаем лишних вызовов API)
        let needsUpdate = false;
        params.encodings.forEach((encoding) => {
            const bitrateChanged = encoding.maxBitrate !== options.maxBitrate;
            const framerateChanged =
                options.maxFramerate !== undefined && encoding.maxFramerate !== options.maxFramerate;

            if (bitrateChanged || framerateChanged) {
                encoding.maxBitrate = options.maxBitrate;
                if (options.maxFramerate !== undefined) {
                    encoding.maxFramerate = options.maxFramerate;
                }
                needsUpdate = true;
            }
        });

        if (needsUpdate) {
            sender.setParameters(params).catch(() => {
                // Игнорируем ошибки - не критично
            });
        }
    } catch {
        // Игнорируем ошибки - не критично
    }
};

/**
 * Проверяет, является ли участник спикером
 * @param participantId - ID участника
 * @param speakerParticipantId - ID текущего спикера (опционально)
 * @returns true, если участник является спикером
 */
export const isParticipantSpeaker = (participantId: string, speakerParticipantId?: string): boolean => {
    return Boolean(speakerParticipantId && participantId === speakerParticipantId);
};

// Внутренняя функция для обратной совместимости
const isSpeaker = isParticipantSpeaker;

export const setLocalCameraQuality = (
    localParticipant: LocalParticipant,
    quality: VideoQuality,
    speakerParticipantId?: string,
    mode?: RoomMode, // Режим комнаты для правильной обработки спикера
): void => {
    const participantIsSpeaker = isSpeaker(localParticipant.identity, speakerParticipantId);
    // В режиме GRID спикер не приоритизируется, качество определяется по количеству участников
    // В режиме SPEAKER/SCREEN_SHARE спикер получает HIGH
    // determineVideoQuality уже учел режим, поэтому просто применяем переданное качество
    // Но в SPEAKER/SCREEN_SHARE режиме защищаем спикера от понижения качества
    if (participantIsSpeaker && mode !== ROOM_MODE.GRID && quality !== VideoQuality.HIGH) {
        // В SPEAKER/SCREEN_SHARE режиме спикер должен публиковать HIGH
        return;
    }

    // Оптимизация: используем getTrackPublication вместо итерации по всем публикациям
    const cameraPublication = localParticipant.getTrackPublication(Track.Source.Camera);
    if (cameraPublication instanceof LocalTrackPublication) {
        const track = cameraPublication.track;
        if (track instanceof LocalVideoTrack) {
            // При simulcast все слои (LOW, MEDIUM, HIGH) публикуются одновременно
            // setPublishingQuality устанавливает приоритет слоя, но не ограничивает выбор удаленных участников
            // Удаленные участники выбирают качество через setVideoQuality на своей стороне
            // Локальный участник всегда видит себя в хорошем качестве (качество захвата с камеры)
            track.setPublishingQuality?.(quality);
        }
    }
};

export const setCameraQuality = (
    participant: Participant,
    quality: VideoQuality,
    speakerParticipantId?: string,
    mode?: RoomMode, // Режим комнаты для правильной обработки спикера
): void => {
    if (participant.isLocal) return;

    const participantIsSpeaker = isSpeaker(participant.identity, speakerParticipantId);
    // В режиме GRID спикер не приоритизируется, качество определяется по количеству участников
    // В режиме SPEAKER/SCREEN_SHARE спикер получает HIGH
    const finalQuality = participantIsSpeaker && mode !== ROOM_MODE.GRID ? VideoQuality.HIGH : quality;

    // Оптимизация: используем getTrackPublication вместо итерации по всем публикациям
    const cameraPublication = participant.getTrackPublication(Track.Source.Camera);
    if (
        !cameraPublication ||
        !cameraPublication.isSubscribed ||
        !(cameraPublication instanceof RemoteTrackPublication)
    ) {
        return;
    }

    const extended = cameraPublication as ExtendedRemoteTrackPublication;
    if (extended.setVideoQuality && extended.videoQuality !== finalQuality) {
        extended.setVideoQuality(finalQuality);
    }
};

export const setLocalScreenShareQuality = (
    localParticipant: LocalParticipant,
    quality: VideoQuality = VideoQuality.HIGH,
): void => {
    const maxBitrate = getScreenShareBitrate(quality);

    // Оптимизация: используем getTrackPublication вместо итерации по всем публикациям
    const screenSharePublication = localParticipant.getTrackPublication(Track.Source.ScreenShare);
    if (screenSharePublication instanceof LocalTrackPublication) {
        const track = screenSharePublication.track;
        if (track instanceof LocalVideoTrack) {
            track.setPublishingQuality?.(quality);

            // sender может быть доступен через внутренние свойства трека
            const sender = (track as unknown as { sender?: RTCRtpSender }).sender;
            if (sender) {
                applyRtpSenderParameters(sender, { maxBitrate });
            }
        }
    }
};

export const getRoomMode = (
    isScreenSharing: boolean,
    speakerParticipantId?: string,
    stageLayout?: 'grid' | 'speaker',
): RoomMode => {
    if (isScreenSharing && speakerParticipantId) return ROOM_MODE.SCREEN_SHARE;
    // Если явно указан grid режим, игнорируем speakerParticipantId
    if (stageLayout === 'grid') return ROOM_MODE.GRID;
    if (speakerParticipantId) return ROOM_MODE.SPEAKER;
    return ROOM_MODE.GRID;
};

export const getGridQuality = (totalParticipants: number): VideoQuality => {
    if (totalParticipants <= VIDEO_QUALITY_THRESHOLDS.HIGH_MAX_PARTICIPANTS) {
        return VideoQuality.HIGH;
    }
    if (totalParticipants <= VIDEO_QUALITY_THRESHOLDS.MEDIUM_MAX_PARTICIPANTS) {
        return VideoQuality.MEDIUM;
    }
    return VideoQuality.LOW;
};

interface QualityContext {
    mode: RoomMode;
    totalParticipants: number;
    participantId: string;
    speakerParticipantId?: string;
    isSidebarMode?: boolean;
}

export const determineVideoQuality = (context: QualityContext): VideoQuality => {
    const { mode, totalParticipants, participantId, speakerParticipantId, isSidebarMode } = context;

    // В режиме grid - качество зависит от количества участников, спикер не приоритизируется
    if (mode === ROOM_MODE.GRID) {
        // Большие комнаты или сайдбар - всегда LOW
        if (totalParticipants > LARGE_ROOM_PARTICIPANTS_THRESHOLD || isSidebarMode) {
            return VideoQuality.LOW;
        }
        return getGridQuality(totalParticipants);
    }

    // В режиме screen share или speaker - спикер в HIGH, остальные в LOW
    if (mode === ROOM_MODE.SCREEN_SHARE || mode === ROOM_MODE.SPEAKER) {
        if (isParticipantSpeaker(participantId, speakerParticipantId)) {
            return VideoQuality.HIGH;
        }
        return VideoQuality.LOW;
    }

    // Fallback: по умолчанию LOW
    return VideoQuality.LOW;
};

export const applyVideoQuality = (participant: Participant, context: Omit<QualityContext, 'participantId'>): void => {
    const quality = determineVideoQuality({ ...context, participantId: participant.identity });

    if (participant.isLocal && participant instanceof LocalParticipant) {
        // Передаем mode для правильной обработки спикера в GRID режиме
        setLocalCameraQuality(participant, quality, context.speakerParticipantId, context.mode);
    } else {
        // Передаем mode для правильной обработки спикера в GRID режиме
        setCameraQuality(participant, quality, context.speakerParticipantId, context.mode);
    }
};
